import { NONE_TEXT } from '@/components/displayFields/AxleInfoField';
import { AxleChip } from '@/components/displayFields/chips';
import { formatInitials } from '@/utils/formatting';
import { ReturnComponent } from '@/utils/reactTypes';
import WarningRounded from '@mui/icons-material/WarningRounded';
import { Avatar } from '@mui/material';
import React from 'react';

import {
    INACTIVE_ICON_SX,
    InactiveAvatar,
    InactiveAvatarBadge,
    InactiveChipTag,
    InactiveLabel,
    InactiveName,
} from './ClinicianChip.styled';
import { ClinicianInactiveIcon } from './ClinicianInactiveIcon';

export interface IClinicianChip {
    id: string;
    firstName: string;
    lastName: string;
    logoUrl: string | null;
    // Sources that don't select it render as they always have.
    isActive?: boolean;
}

export interface IClinicianChipProps {
    clinician: IClinicianChip | null;
    showLink: boolean;
    wrap?: boolean;
    services?: {
        id: string;
        name: string;
    }[];
    showServices?: boolean;
    color?: string;
    title?: string;
    onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void;
    onMouseLeave?: (event: React.MouseEvent<HTMLElement>) => void;
}

// Memoized because this is rendered once per clinician in dense views like the
// schedule calendar's column headers (hundreds of instances). Those parents
// re-render frequently (e.g. react-big-calendar re-renders its whole grid on
// any event click), and without memoization every chip — plus its Avatar,
// Tooltip and Popper subtree — re-renders even when the clinician hasn't
// changed. The component is pure, so a shallow prop compare is safe.
export const ClinicianChip = React.memo(function ClinicianChip({
    clinician,
    showLink,
    wrap = false,
    services = [],
    showServices = false,
    color,
    title,
    onMouseEnter,
    onMouseLeave,
}: IClinicianChipProps): ReturnComponent {
    const serviceLabel =
        showServices && services.length > 0
            ? ' (' + [...new Set(services.map((service) => service.name))].join(', ') + ')'
            : '';
    if (!clinician) {
        return <AxleChip icon={<WarningRounded />} label={NONE_TEXT + serviceLabel} />;
    }
    const isInactive = clinician.isActive === false;
    const name = `${clinician.firstName} ${clinician.lastName}`.trim();
    const label = `${name} ${serviceLabel}${title ? ` - ${title}` : ''}`;
    const initials = formatInitials(clinician.firstName, clinician.lastName);
    return (
        <AxleChip
            avatar={
                isInactive ? (
                    <InactiveAvatarBadge
                        overlap='circular'
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={<ClinicianInactiveIcon sx={INACTIVE_ICON_SX} />}
                    >
                        <InactiveAvatar src={clinician.logoUrl ?? undefined} alt={name}>
                            {initials}
                        </InactiveAvatar>
                    </InactiveAvatarBadge>
                ) : (
                    <Avatar src={clinician.logoUrl ?? undefined} alt={name}>
                        {initials}
                    </Avatar>
                )
            }
            label={
                isInactive ? (
                    <InactiveLabel>
                        <InactiveName>{label}</InactiveName>
                        <InactiveChipTag />
                    </InactiveLabel>
                ) : (
                    label
                )
            }
            tooltip={isInactive ? `${name} is inactive` : undefined}
            routerLink={showLink ? `/clinicians/${clinician.id}` : undefined}
            sx={{
                backgroundColor: color ? 'FFFFFF' : undefined,
                color: color ?? (isInactive ? 'text.disabled' : undefined),
                border: color ? `2px solid ${color}` : undefined,
                fontWeight: color ? '500' : undefined,
            }}
            wrap={wrap}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        />
    );
});
